import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SHIRT_PRESETS, salesmanSprite } from "@/lib/customers";
import { PixelSprite } from "@/components/game/PixelSprite";
import { selectCls } from "./QuestionEditor";

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
    <span>{label}</span>
    <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
  </label>
);

export const SettingsPanel = ({ quiz, update, t }) => {
  const s = quiz.settings;
  const setS = (k) => (v) => update((qz) => { qz.settings[k] = v; });
  return (
    <aside className="w-96 shrink-0 border-l border-border bg-card/50 overflow-y-auto p-5 flex flex-col gap-6" data-testid="builder-settings-panel">
      <div className="grid gap-3">
        <Label>{t("shirt_color")}</Label>
        <div className="relative h-44 rounded-xl overflow-hidden border border-border">
          <div className="store-bg" />
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2"><PixelSprite params={salesmanSprite(quiz.shirt_color)} height="150px" testId="salesman-preview-sprite" /></div>
        </div>
        <div className="flex flex-wrap gap-3">
          {SHIRT_PRESETS.map((c) => (
            <button key={c} data-testid={`shirt-preset-${c.slice(1)}`} onClick={() => update((qz) => { qz.shirt_color = c; })} title={c}
              className={`w-9 h-9 rounded-md ring-offset-2 ring-offset-card transition-shadow ${quiz.shirt_color.toLowerCase() === c.toLowerCase() ? "ring-2 ring-primary" : "ring-1 ring-white/20"}`}
              style={{ background: c }} />
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{t("settings")}</div>
        <div className="grid gap-2">
          <Label>{t("language")}</Label>
          <select data-testid="quiz-language-select" className={selectCls} value={quiz.language} onChange={(e) => update((qz) => { qz.language = e.target.value; })}>
            <option value="en">{t("english")}</option>
            <option value="fi">{t("finnish")}</option>
          </select>
        </div>
        <div className="grid gap-2">
          <Label>{t("read_time")}</Label>
          <select data-testid="quiz-read-time-select" className={selectCls} value={s.read_time} onChange={(e) => setS("read_time")(Number(e.target.value))}>
            {[0, 3, 5, 7, 10, 15].map((n) => <option key={n} value={n}>{n} {t("seconds")}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">{t("read_time_hint")}</p>
        </div>
        <Toggle label={t("show_scoreboard")} checked={s.show_scoreboard} onChange={setS("show_scoreboard")} testId="setting-show-scoreboard" />
        <Toggle label={t("auto_advance")} checked={s.auto_advance} onChange={setS("auto_advance")} testId="setting-auto-advance" />
        <Toggle label={t("options_on_phone")} checked={s.options_on_phone} onChange={setS("options_on_phone")} testId="setting-options-on-phone" />
        <Toggle label={t("music")} checked={s.music} onChange={setS("music")} testId="setting-music" />
        <div className="grid gap-2">
          <Label>{t("collect_contacts")}</Label>
          <select data-testid="quiz-collect-contacts-select" className={selectCls} value={s.collect_top_contacts ?? 0}
            onChange={(e) => setS("collect_top_contacts")(Number(e.target.value))}>
            <option value={0}>{t("collect_off")}</option>
            {[1, 2, 3].map((n) => <option key={n} value={n}>{t("collect_top_n", { n })}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">{t("collect_contacts_hint")}</p>
        </div>
        {quiz.is_owner && (
          <div className="grid gap-2">
            <Label>{t("admin_access")}</Label>
            <select data-testid="quiz-access-select" className={selectCls} value={quiz.access || "no_access"} onChange={(e) => update((qz) => { qz.access = e.target.value; })}>
              <option value="no_access">{t("access_none")}</option>
              <option value="host_copy">{t("access_host_copy")}</option>
              <option value="edit">{t("access_edit")}</option>
            </select>
            <p className="text-xs text-muted-foreground">{t("admin_access_hint")}</p>
          </div>
        )}
        <div className="grid gap-2">
          <Label>{t("quiz_description")}</Label>
          <Textarea data-testid="quiz-description-input" rows={2} maxLength={200} value={quiz.description} onChange={(e) => update((qz) => { qz.description = e.target.value; })} />
        </div>
      </div>
    </aside>
  );
};
